import { z } from "zod";
import {
  CalendarDateSchema,
  TimeOfDaySchema,
  TranslationMapSchema,
} from "./utils";

/**
 * When and how often a study prompts a patient for one of its surveys.
 *
 * The unit of the whole design is the OCCURRENCE: a specific
 * (patient, scheduled survey, index) with an instant it opens at and an
 * instant it closes at. Occurrences are never stored. They are derived from
 * the rule below plus a per-patient anchor, which is sound because a study's
 * content freezes on activation, so the rule can never change under a series
 * that is already running. The payoff shows up when a surgery is postponed:
 * move the date and every future occurrence moves with it, while answers
 * already submitted stay attached to their index ("week 2 post-op") rather
 * than to a calendar date that no longer means anything.
 *
 * The rule is five orthogonal parts. Free text ("bi-monthly") is what this
 * replaces, because it cannot say whether it means twice a month or every two.
 *
 *   anchor + offset   where the series starts, per patient
 *   repeat            how often it comes back, or once
 *   end               when it stops
 *   timeOfDay         what clock an occurrence opens on
 *   windowMinutes     how long it stays answerable
 */

/**
 * What t0 is measured from.
 *
 * `ENROLMENT` is the default for a reason: in a longitudinal study "the week-4
 * questionnaire" means four weeks after THAT patient started, not four weeks
 * after the study opened, and patients enrol on different days.
 *
 * It anchors on the LATER of the enrolment and the study's activation, which
 * is what answers the old objection to enrolment (a cohort lined up while the
 * study was still a draft would otherwise arrive to a fortnight of windows
 * that opened before the study existed).
 *
 * It REPLACED a `CONSENT` anchor on 2026-08-11, and the reason is worth
 * keeping. Anchoring on consent made an unconsented patient generate no
 * occurrences at all, which made them unreachable: no occurrence, no open
 * window, no reminder, no way to learn they had been enrolled. Consent also
 * became a clock a patient could move — the consent endpoint is deliberately
 * non-idempotent, so re-signing re-anchored every future occurrence. A
 * protocol says "week 4 after inclusion", never "week 4 after the patient
 * happened to sign", and consent remains a gate on COLLECTION where it
 * belongs (`submitSurveyResponse`), not on visibility.
 *
 * A per-patient reference date (an operation date) was an anchor too, and is
 * the only way to schedule a survey BEFORE a date that is not the same for the
 * whole cohort. It was removed on purpose while scheduling settles; the design
 * is kept in docs/deferred/patient-reference-dates.md.
 */
export const ScheduleAnchorSchema = z.enum([
  "ENROLMENT",
  "STUDY_ACTIVATION",
  "FIXED_DATE",
  "MANUAL",
]);
export type ScheduleAnchor = z.infer<typeof ScheduleAnchorSchema>;

export const ScheduleUnitSchema = z.enum(["DAY", "WEEK", "MONTH"]);
export type ScheduleUnit = z.infer<typeof ScheduleUnitSchema>;

export const ScheduleEndSchema = z.enum([
  "STUDY_CLOSE",
  "ON_DATE",
  "AFTER_N",
]);
export type ScheduleEnd = z.infer<typeof ScheduleEndSchema>;

/**
 * The shortest a unit can possibly be, in minutes. Used only to check a window
 * against its period, where being conservative is the safe direction: a
 * 28-day February is the tightest a monthly period ever gets, so a window that
 * fits it fits every other month too.
 */
const MIN_UNIT_MINUTES: Record<ScheduleUnit, number> = {
  DAY: 24 * 60,
  WEEK: 7 * 24 * 60,
  MONTH: 28 * 24 * 60,
};

/** The tightest this rule's period can ever be, or null when it is one-shot. */
export function minPeriodMinutes(rule: {
  repeatEvery?: number | null;
  repeatUnit?: ScheduleUnit | null;
}): number | null {
  if (!rule.repeatEvery || !rule.repeatUnit) return null;
  return rule.repeatEvery * MIN_UNIT_MINUTES[rule.repeatUnit];
}

const ScheduleShape = {
  anchor: ScheduleAnchorSchema,
  /** Required by, and only by, `FIXED_DATE`. */
  anchorDate: CalendarDateSchema.nullish(),
  /** Signed: -21 DAY is "three weeks before the anchor". */
  offset: z.number().int().default(0),
  offsetUnit: ScheduleUnitSchema.default("DAY"),

  /** Both null for a one-shot. `repeatEvery: 2, repeatUnit: "WEEK"` is
   *  unambiguous where "bi-monthly" is not. */
  repeatEvery: z.number().int().positive().nullish(),
  repeatUnit: ScheduleUnitSchema.nullish(),

  end: ScheduleEndSchema.default("STUDY_CLOSE"),
  endDate: CalendarDateSchema.nullish(),
  /** `AFTER_N`: protocols are written as "12 weekly measurements". */
  endCount: z.number().int().positive().nullish(),

  timeOfDay: TimeOfDaySchema.default("08:00"),

  /**
   * How long an occurrence stays answerable. `null` means "until the next one
   * opens", which is the tiling default a daily survey wants, or until the
   * study closes for a one-shot.
   */
  windowMinutes: z.number().int().positive().nullish(),
};

/**
 * Everything a rule must satisfy to be coherent, shared by the API and the
 * dashboard form so a form showing all green next to a 400 is impossible
 * (same reasoning as `password.ts`).
 */
function refineSchedule(
  rule: {
    anchor: ScheduleAnchor;
    anchorDate?: string | null;
    offset: number;
    repeatEvery?: number | null;
    repeatUnit?: ScheduleUnit | null;
    end: ScheduleEnd;
    endDate?: string | null;
    endCount?: number | null;
    windowMinutes?: number | null;
  },
  ctx: z.RefinementCtx,
): void {
  const fail = (path: string, message: string) =>
    ctx.addIssue({ code: "custom", path: [path], message });

  if (rule.anchor === "FIXED_DATE" && !rule.anchorDate)
    fail("anchorDate", "A FIXED_DATE schedule needs anchorDate");
  if (rule.anchor !== "FIXED_DATE" && rule.anchorDate)
    fail("anchorDate", `anchorDate is meaningless for a ${rule.anchor} anchor`);

  // A negative offset only has something to be negative against when the
  // anchor is a date known in advance. "Three weeks before consent" cannot be
  // scheduled, because nothing knows consent is coming.
  if (rule.offset < 0 && rule.anchor !== "FIXED_DATE")
    fail(
      "offset",
      `A negative offset needs an anchor known in advance (FIXED_DATE), not ${rule.anchor}`,
    );

  const recurring = Boolean(rule.repeatEvery);
  if (Boolean(rule.repeatEvery) !== Boolean(rule.repeatUnit))
    fail("repeatUnit", "repeatEvery and repeatUnit go together or not at all");

  if (!recurring && rule.end !== "STUDY_CLOSE")
    fail("end", "A one-shot schedule ends when it fires; leave end at STUDY_CLOSE");

  if (rule.end === "AFTER_N" && !rule.endCount)
    fail("endCount", "An AFTER_N schedule needs endCount");
  if (rule.end !== "AFTER_N" && rule.endCount)
    fail("endCount", "endCount only applies to an AFTER_N schedule");

  if (rule.end === "ON_DATE" && !rule.endDate)
    fail("endDate", "An ON_DATE schedule needs endDate");
  if (rule.end !== "ON_DATE" && rule.endDate)
    fail("endDate", "endDate only applies to an ON_DATE schedule");

  // The rule that keeps "at most one open occurrence per scheduled survey"
  // true: a window longer than the period would leave two instances of the
  // same survey answerable at once, and neither the patient nor the export
  // could say which one an answer belongs to.
  const period = minPeriodMinutes(rule);
  if (period !== null && rule.windowMinutes && rule.windowMinutes > period)
    fail(
      "windowMinutes",
      `The answering window (${rule.windowMinutes} min) cannot outlast the period it repeats on (${period} min)`,
    );
}

export const ScheduleInputSchema = z
  .object(ScheduleShape)
  .superRefine(refineSchedule);
export type ScheduleInput = z.infer<typeof ScheduleInputSchema>;

/** The stored rule as it comes back out, with the nullables settled. */
export const ScheduleSchema = z.object({
  anchor: ScheduleAnchorSchema,
  anchorDate: CalendarDateSchema.nullable(),
  offset: z.number().int(),
  offsetUnit: ScheduleUnitSchema,
  repeatEvery: z.number().int().nullable(),
  repeatUnit: ScheduleUnitSchema.nullable(),
  end: ScheduleEndSchema,
  endDate: CalendarDateSchema.nullable(),
  endCount: z.number().int().nullable(),
  timeOfDay: TimeOfDaySchema,
  windowMinutes: z.number().int().nullable(),
});
export type Schedule = z.infer<typeof ScheduleSchema>;

/**
 * `SUBMITTED` and `MISSED` are both terminal: the window closes hard. A late
 * PROM answer carries recall bias and is worth less than a recorded miss,
 * and the miss count is the compliance denominator a study actually reports.
 */
export const OccurrenceStatusSchema = z.enum([
  "UPCOMING",
  "OPEN",
  "SUBMITTED",
  "MISSED",
]);
export type OccurrenceStatus = z.infer<typeof OccurrenceStatusSchema>;

export const SurveyOccurrenceSchema = z.object({
  scheduledSurveyId: z.string(),
  /** Opaque to clients. Identifies the occurrence on submission. */
  key: z.string(),
  index: z.number().int(),
  opensAt: z.coerce.date(),
  /** Null while nothing bounds it: no window, no next occurrence, study open. */
  closesAt: z.coerce.date().nullable(),
  status: OccurrenceStatusSchema,
  submittedAt: z.coerce.date().nullable(),
});
export type SurveyOccurrence = z.infer<typeof SurveyOccurrenceSchema>;

/**
 * One scheduled survey as a patient's app sees it: what is answerable now, and
 * what is coming. `upcoming` is what makes "next on Monday" possible without a
 * second request, and it is free because occurrences are computed anyway.
 */
export const ScheduledSurveyStateSchema = z.object({
  scheduledSurveyId: z.string(),
  surveyId: z.string(),
  key: z.string(),
  label: TranslationMapSchema,
  /** Null when nothing is answerable right now. */
  current: SurveyOccurrenceSchema.nullable(),
  upcoming: z.array(SurveyOccurrenceSchema),
  /** Submitted and missed occurrences, most recent first. */
  history: z.array(SurveyOccurrenceSchema),
  /**
   * True when the rule can never produce an occurrence as things stand: a
   * MANUAL schedule nobody has triggered. The dashboard turns this into a work
   * item, otherwise a study quietly collects nothing from that patient.
   */
  awaitingAnchor: z.boolean(),
});
export type ScheduledSurveyState = z.infer<typeof ScheduledSurveyStateSchema>;

export const TriggerSurveyInputSchema = z.object({
  note: z.string().max(500).optional(),
});
export type TriggerSurveyInput = z.infer<typeof TriggerSurveyInputSchema>;
